import { cn } from "@/engine/support/classNames";

import { AnimalFigure } from "./AnimalFigure";
import { FireStage, type StagePerson } from "./FireStage";
import { Glade, type GladeLight } from "@/components/room/Glade";
import type { AnimalId, ColourId, PlaceItemId, PlaceSlot } from "./campData";
import type { RoomMode } from "./RoomModePicker";

export interface RoomStagePerson {
  id: string;
  name: string;
  animal: AnimalId;
  colour: ColourId;
  speaking?: boolean | undefined;
  isYou?: boolean | undefined;
  distant?: boolean | undefined;
  team?: "one" | "two" | "hall" | undefined;
  place?: Partial<Record<PlaceSlot, PlaceItemId>> | undefined;
  /** Where somebody stands in Bubbles; see `StagePerson.position`. */
  position?: StagePerson["position"];
  note?: string | undefined;
}

export interface RoomModeStageProps extends React.HTMLAttributes<HTMLDivElement> {
  mode: RoomMode;
  people: readonly RoomStagePerson[];
  teamOneLabel: string;
  teamTwoLabel: string;
  hallLabel: string;
}

function Person({
  person,
  size = "default",
}: {
  person: RoomStagePerson;
  size?: "default" | "compact" | "team";
}) {
  return (
    <div
      className={cn(
        "relative flex min-w-0 flex-col items-center gap-1",
        size === "team" && "w-full flex-1 basis-0",
        person.distant && "opacity-40",
      )}
    >
      <AnimalFigure
        animal={person.animal}
        colour={person.colour}
        place={person.place}
        pose={person.speaking ? "talk" : "stand"}
        dim={person.distant}
        className={cn(
          size === "default" && "h-32 w-24",
          size === "compact" && "h-24 w-[4.5rem]",
          size === "team" && "h-[4.5rem] w-full max-w-16",
        )}
      />
      {/* The figure stands on its glade's ground, so it casts a shadow there. */}
      <span
        aria-hidden="true"
        className={cn(
          "-mt-2.5 h-2.5 rounded-[50%] bg-[radial-gradient(ellipse,hsl(var(--overlay)/0.7),transparent_70%)]",
          size === "team" ? "w-12 -mt-2" : "w-20",
        )}
      />
      <span
        className={cn(
          "w-full max-w-24 truncate text-center text-xs",
          person.isYou ? "text-you" : "text-foreground",
        )}
      >
        {person.name}
      </span>
    </div>
  );
}

function Fire({
  people,
  bubbles,
}: {
  people: readonly RoomStagePerson[];
  bubbles: boolean;
}) {
  const others = people.filter((person) => !person.isYou);
  const you = people.find((person) => person.isYou);
  const ordered = you ? [you, ...others] : others;
  const stagePeople: StagePerson[] = ordered.map((person) => ({
    id: person.id,
    name: person.name,
    beast: person.animal,
    colour: person.colour,
    speaking: person.speaking,
    isYou: person.isYou,
    faded: person.distant,
    position: person.position,
    note: person.note,
  }));
  return (
    <FireStage
      people={stagePeople}
      mode={bubbles ? "bubbles" : "fire"}
      className="h-full min-h-0"
    />
  );
}

function Plain({ people }: { people: readonly RoomStagePerson[] }) {
  return (
    <div className="grid h-full min-h-0 grid-cols-2 gap-3">
      {people.map((person) => (
        <div
          key={person.id}
          className="relative grid min-h-40 place-items-center overflow-hidden rounded-2xl bg-background p-3"
        >
          <Glade light="fire" />
          <Person person={person} />
        </div>
      ))}
    </div>
  );
}

const EDGE: Record<GladeLight, string> = {
  fire: "border-you/25",
  "team-1": "border-team-1/30",
  "team-2": "border-team-2/30",
};

function Teams({
  people,
  labels,
}: {
  people: readonly RoomStagePerson[];
  labels: readonly [string, string, string];
}) {
  const groups = ["one", "hall", "two"] as const;
  // Your own team is green (team-1) and the other red (team-2), whichever of
  // the two you stand in. From the hall neither is yours, and the order holds.
  const ownTeamIsTwo = people.some(
    (person) => person.isYou && person.team === "two",
  );
  const lights: readonly GladeLight[] = ownTeamIsTwo
    ? ["team-2", "fire", "team-1"]
    : ["team-1", "fire", "team-2"];
  return (
    <div className="grid h-full min-h-0 grid-rows-3 gap-1">
      {groups.map((group, index) => (
        <section
          key={group}
          aria-label={labels[index]}
          className={cn(
            "relative flex min-h-0 items-center overflow-hidden rounded-2xl border bg-background p-2 sm:p-3",
            EDGE[lights[index] ?? "fire"],
          )}
        >
          <Glade light={lights[index] ?? "fire"} />
          <div className="relative flex h-full min-w-0 flex-1 flex-nowrap items-center justify-center gap-0.5 overflow-hidden sm:gap-1">
            {people
              .filter((person) => (person.team ?? "hall") === group)
              .map((person) => (
                <Person key={person.id} person={person} size="team" />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Full room centre for fire, bubbles, plain-call and team layouts. */
export function RoomModeStage({
  mode,
  people,
  teamOneLabel,
  teamTwoLabel,
  hallLabel,
  className,
  ...props
}: RoomModeStageProps) {
  return (
    // Gives way on a short window, like the fire stage, so the room's controls stay on screen.
    <div
      className={cn("min-h-[min(360px,45dvh)] flex-1", className)}
      {...props}
    >
      {mode === "plain" ? (
        <Plain people={people} />
      ) : mode === "teams" ? (
        <Teams
          people={people}
          labels={[teamOneLabel, hallLabel, teamTwoLabel]}
        />
      ) : (
        <Fire people={people} bubbles={mode === "bubbles"} />
      )}
    </div>
  );
}
